import { useEffect, useMemo, useRef, useState } from 'react'
import { motion } from 'framer-motion'

import CircularProgress from '@mui/material/CircularProgress'

import MetaTitle from '../../Components/UI/MetaTitle'
import PseudoHeader from '../../Components/PseudoHeader/PseudoHeader'
import LoadingSpinner from '../../Components/LoadingSpinner'

import GoogleMap from '../../Components/Recommendations/GoogleMap'
import ActionBar from '../../Components/Recommendations/ActionBar'
import ConciergeAIInfoBox from '../../Components/Recommendations/ConciergeAIInfoBox'
import HowItWorksDialog from '../../Components/Recommendations/HowItWorksDialog'
import VenueFilterRow from '../../Components/Recommendations/VenueFilterRow'
import VenueList, { SpaceInterest } from '../../Components/Recommendations/VenueList'
import VenueSearchInput, { SearchMode } from '../../Components/Recommendations/VenueSearchInput'

import useBriefContext from '../../Hooks/Brief/useBriefContext'
import useSpaceRecommendations, {
  Space,
} from '../../Hooks/useRecommendation/useSpaceRecommendations'
import useSpaceRecommendationSearch from '../../Hooks/useRecommendation/useSpaceRecommendationSearch'
import useSubmitBrief from '../../Hooks/Brief/useSubmitBrief'
import useRegisterUser from '../../Hooks/Brief/useRegisterUser'
import { ShortlistSpace, useShortlistedSpaces } from '../../Hooks/useShortlist'
import { clearStoredShortlist } from '../../Utilities/Storage/shortlist'
import { MAX_REQUESTED_VENUES } from '../../Constants'
import InputView from '../FormSteps/InputView'
import { useFacilitiesQuery } from '../FormSteps/Facilities/Base/Facilities/FacilitiesInput.generated'
import './RecommendationInput.less'
import { ScrollTop } from '@/Components/UI/Icons'

const RecommendationInput: InputView = (): JSX.Element => {
  const { brief } = useBriefContext()
  const [page, setPage] = useState(1)
  const [requestedSpaces, setRequestedSpaces] = useState<SpaceInterest[]>([])
  const [shouldRegisterUser, setShouldRegisterUser] = useState(false)
  const [shouldSubmitBrief, setShouldSubmitBrief] = useState(false)
  const [howItWorksOpen, setHowItWorksOpen] = useState(false)
  const [searchMode, setSearchMode] = useState<SearchMode>('prompt')
  const [promptSearchQuery, setPromptSearchQuery] = useState('')
  const [inputValue, setInputValue] = useState('')
  const [promptFilters, setPromptFilters] = useState<string[]>([])
  const [hasSearched, setHasSearched] = useState(false)
  const [focusedSpaceId, setFocusedSpaceId] = useState<number | null>(null)
  const [autoSubmitting, setAutoSubmitting] = useState(false)
  const seededShortlist = useRef(false)
  const { registering, registered } = useRegisterUser({ skip: !shouldRegisterUser })

  const [{ data: facilitiesData }] = useFacilitiesQuery({
    pause: !brief.collection,
    requestPolicy: 'cache-first',
    variables: { id: brief.collection ?? '' },
  })

  const selectedFacilityIds = brief.requiredSpaceFacilityIds?.length
    ? brief.requiredSpaceFacilityIds
    : brief.spaceFacilityIds?.length
    ? brief.spaceFacilityIds
    : brief.optionalSpaceFacilityIds

  const requiredFacilityNames = useMemo(() => {
    if (!facilitiesData?.collection?.features || !selectedFacilityIds?.length) return []
    return facilitiesData.collection.features
      .filter((f) => selectedFacilityIds.includes(parseInt(f.id, 10)))
      .map((f) => f.name)
  }, [facilitiesData, selectedFacilityIds])

  useEffect(() => {
    setPromptFilters(requiredFacilityNames)
  }, [requiredFacilityNames])

  const activeFilters = searchMode === 'venue-name' && hasSearched ? [] : promptFilters

  const onFiltersChange = (filters: string[]) => {
    if (searchMode === 'venue-name') return
    setPromptFilters(filters)
  }

  const activeFacilityIds = useMemo(() => {
    if (!facilitiesData?.collection?.features || !activeFilters.length) return []
    return facilitiesData.collection.features
      .filter((f) => activeFilters.includes(f.name))
      .map((f) => parseInt(f.id, 10))
  }, [facilitiesData, activeFilters])

  const unselectedFacilities = useMemo(() => {
    if (!facilitiesData?.collection?.features) return []
    return facilitiesData.collection.features.filter((f) => !activeFilters.includes(f.name))
  }, [facilitiesData, activeFilters])

  const {
    recommendations,
    loading: recsLoading,
    error: recsError,
    prompt: aiPrompt,
  } = useSpaceRecommendations(brief, promptSearchQuery, activeFilters, activeFacilityIds)

  const [defaultRecommendations, setDefaultRecommendations] = useState<Space[]>([])
  const hasCapturedDefaultRef = useRef(false)

  useEffect(() => {
    if (!hasCapturedDefaultRef.current && recommendations.length > 0) {
      setDefaultRecommendations(recommendations)
      hasCapturedDefaultRef.current = true
    }
  }, [recommendations])

  const {
    spaces: searchResults,
    loading: searchLoading,
    search,
  } = useSpaceRecommendationSearch(brief)
  const [lastSearchQuery, setLastSearchQuery] = useState<string | null>(null)

  const displayedRecommendations =
    searchMode === 'venue-name' && hasSearched ? searchResults : recommendations
  const loading = searchMode === 'venue-name' && hasSearched ? searchLoading : recsLoading
  const displayError = searchMode === 'venue-name' && hasSearched ? null : recsError

  const visibleRecommendations =
    displayedRecommendations.length === 0 ? defaultRecommendations : displayedRecommendations

  const activePrompt = lastSearchQuery ?? aiPrompt

  const allSpaces = useMemo(() => {
    const seen = new Set<string>()
    const result: Space[] = []
    for (const s of [...recommendations, ...searchResults, ...defaultRecommendations]) {
      if (!seen.has(s.id)) {
        seen.add(s.id)
        result.push(s)
      }
    }
    return result
  }, [recommendations, searchResults, defaultRecommendations])

  const { spaces: shortlistSpaces, fetching: shortlistFetching } = useShortlistedSpaces(allSpaces)

  const shortlistedSpaces = useMemo(() => {
    const byId = new Map<number, ShortlistSpace>()
    for (const space of [...allSpaces, ...shortlistSpaces]) byId.set(Number(space.id), space)

    return requestedSpaces
      .map(({ spaceId }) => byId.get(spaceId))
      .filter((space): space is ShortlistSpace => space !== undefined)
  }, [allSpaces, shortlistSpaces, requestedSpaces])

  const { loading: submitting, submitted } = useSubmitBrief({
    skip: !shouldSubmitBrief,
    venueRequests: requestedSpaces,
    prompt: activePrompt,
  })

  useEffect(() => {
    if (submitted) clearStoredShortlist()
  }, [submitted])

  useEffect(() => {
    if (!registered || !shouldRegisterUser || submitting) return
    setShouldSubmitBrief(true)
  }, [registered])

  const onSearch = () => {
    if (searchMode === 'prompt') {
      const query = inputValue.trim()
      if (query) {
        setLastSearchQuery(query)
        setPromptSearchQuery(query)
      }
      return
    }
    const query = inputValue.trim()
    if (query) {
      setHasSearched(true)
      setLastSearchQuery(query)
      search(query, aiPrompt)
    }
  }

  const onSearchModeChange = (mode: SearchMode) => {
    setSearchMode(mode)
    if (mode === 'prompt') setHasSearched(false)
  }

  const showLoadingState = submitting || registering || autoSubmitting

  const onSkip = () => {
    setRequestedSpaces([])
    if (!brief.guest?.id) return setShouldRegisterUser(true)
    setShouldSubmitBrief(true)
  }

  const onRequestVenues = () => {
    if (!brief.guest?.id) return setShouldRegisterUser(true)
    setShouldSubmitBrief(true)
  }

  const onToggleSpace = (spaceId: number, hostId: number) => {
    setRequestedSpaces((prev) =>
      prev.some((s) => s.spaceId === spaceId)
        ? prev.filter((s) => s.spaceId !== spaceId)
        : [...prev, { spaceId, hostId }]
    )
  }

  useEffect(() => {
    if (seededShortlist.current || !shortlistSpaces.length) return

    seededShortlist.current = true
    setRequestedSpaces((requested) => {
      const merged = [...requested]

      for (const space of shortlistSpaces) {
        if (merged.length >= MAX_REQUESTED_VENUES) break
        if (merged.some(({ spaceId }) => spaceId === Number(space.id))) continue

        merged.push({ spaceId: Number(space.id), hostId: space.hostID })
      }

      return merged
    })
  }, [shortlistSpaces])

  // Empty first fetch means nothing to shortlist - the default guard stops later searches firing it.
  useEffect(() => {
    if (recsLoading || shortlistFetching || autoSubmitting) return
    if (recommendations.length || defaultRecommendations.length) return

    setAutoSubmitting(true)
    if (shortlistSpaces.length) return onRequestVenues()

    onSkip()
  }, [recsLoading, shortlistFetching, recommendations, defaultRecommendations, shortlistSpaces])

  return (
    <>
      {showLoadingState && (
        <LoadingSpinner
          fullPage={true}
          accessibleText={registering ? 'Creating your account...' : 'Submitting your enquiry...'}
        />
      )}
      <MetaTitle title='Recommended venues - HeadBox' />
      <div className={showLoadingState ? 'hidden' : ''}>
        <PseudoHeader />
        <div className='relative flex-1 mt-16 pt-6 md:pt-10'>
          <div
            className={`w-full px-4 lg:pl-20 lg:pr-8 flex lg:gap-14
              pb-[calc(var(--action-bar-height,9rem)+1.5rem)]`}>
            <div className='flex flex-col gap-6 flex-1 min-w-0'>
              <motion.h2
                initial={{ opacity: 0, y: -15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2, ease: 'easeOut' }}
                style={{
                  background: 'linear-gradient(90deg, #000 0%, #AF231C 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
                className='text-2xl font-semibold leading-8'>
                Here are some venue options for you to review:
              </motion.h2>

              <ConciergeAIInfoBox onHowItWorksClick={() => setHowItWorksOpen(true)} />

              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.45, ease: 'easeOut' }}
                className='flex flex-col gap-2'>
                <VenueSearchInput
                  value={inputValue}
                  onChange={setInputValue}
                  onSearch={onSearch}
                  loading={loading}
                  searchMode={searchMode}
                  onSearchModeChange={onSearchModeChange}
                />
                <VenueFilterRow
                  unselectedFacilities={unselectedFacilities}
                  activeFilters={activeFilters}
                  onFiltersChange={onFiltersChange}
                  totalCount={displayedRecommendations.length}
                  showFacilities={searchMode !== 'venue-name'}
                />
              </motion.div>

              {loading && displayedRecommendations.length === 0 ? (
                <LoadingSpinner accessibleText='Finding venues for you...' />
              ) : displayError ? (
                <p className='text-base text-red-600'>{displayError}</p>
              ) : (
                <div className='relative'>
                  {loading && (
                    <div
                      className={`absolute inset-0 z-10 bg-white/60 rounded-lg flex
                        items-start justify-center pt-16 pointer-events-none`}>
                      <CircularProgress size={40} sx={{ color: '#af231c' }} />
                    </div>
                  )}
                  <VenueList
                    recommendations={recommendations}
                    filteredRecommendations={displayedRecommendations}
                    defaultRecommendations={defaultRecommendations}
                    page={page}
                    onLoadMore={() => setPage((p) => p + 1)}
                    requestedSpaces={requestedSpaces}
                    onToggleSpace={onToggleSpace}
                    onCardClick={setFocusedSpaceId}
                  />
                </div>
              )}
            </div>

            <aside className='hidden lg:block w-5/12 shrink-0'>
              <div className='sticky top-[104px] h-[calc(100vh-200px)] flex flex-col gap-4'>
                <div className='flex-1 min-h-0'>
                  <GoogleMap
                    spaces={visibleRecommendations}
                    initialLocation={
                      brief.lat && brief.lon ? { lat: brief.lat, lon: brief.lon } : null
                    }
                    selectedSpaceIds={requestedSpaces.map((s) => s.spaceId)}
                    focusedSpaceId={focusedSpaceId}
                  />
                </div>
                <div className='flex justify-end items-center gap-2'>
                  <ScrollTop />
                  <button
                    type='button'
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className='text-base font-medium text-[#1270B8] hover:underline text-left'>
                    Back to top
                  </button>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>

      <ActionBar
        onSkip={onSkip}
        onConfirm={onRequestVenues}
        confirmLabel={`Request ${requestedSpaces.length} venue${
          requestedSpaces.length !== 1 ? 's' : ''
        }`}
        confirmDisabled={requestedSpaces.length === 0}
        disabled={showLoadingState}
        shortlistedSpaces={shortlistedSpaces}
        onRemoveVenue={(space) => onToggleSpace(Number(space.id), space.hostID)}
      />

      <HowItWorksDialog isOpen={howItWorksOpen} onClose={() => setHowItWorksOpen(false)} />
    </>
  )
}

export default RecommendationInput
